/**
 * Phase K — /mishap-corrector/programs/{slug}
 *
 * Public landing page for each verified mishap-corrector destination program.
 * Renders Course JSON-LD and links to all pair pages that target this program.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function buildCourseJsonLd(program, origin) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: program.name,
    description: program.notes || `${program.name} transfer pathway.`,
    provider: {
      "@type": "EducationalOrganization",
      name: (program.destination_institution_slug || "").toUpperCase(),
      sameAs: `${origin}/universities/${program.destination_institution_slug || ""}`,
    },
    url: `${origin}/mishap-corrector/programs/${program.slug}`,
  };
}

function buildBreadcrumbJsonLd(program, origin) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
      { "@type": "ListItem", position: 2, name: "Mishap Corrector", item: origin + "/mishap-corrector" },
      { "@type": "ListItem", position: 3, name: program.name, item: origin + `/mishap-corrector/programs/${program.slug}` },
    ],
  };
}

export default function MishapProgramLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    axios.get(`${API}/catalog/mishap-program-pages/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setNotFound(e?.response?.status === 404));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-bone">
        <SEO noindex title="Program not found" canonical={`/mishap-corrector/programs/${slug}`} />
        <TopNav />
        <main className="max-w-3xl mx-auto px-5 py-24">
          <h1 className="font-serif text-4xl text-ivy">Program not available</h1>
          <p className="mt-4 text-ink-soft">This program isn&apos;t verified in our catalog yet.</p>
          <Link to="/mishap-corrector" className="mt-6 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6">
            Back to Mishap Corrector
          </Link>
        </main>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-bone">
        <TopNav />
        <div className="max-w-4xl mx-auto px-5 py-24 text-ink-soft">Loading program guide…</div>
      </div>
    );
  }

  const p = data.program;
  const origin = process.env.REACT_APP_PUBLIC_ORIGIN || "https://ivyleagueconsulting.org";
  const prereqCourses = data.requirements.filter((r) => r.kind === "prereq_course");
  const gpaReqs = data.requirements.filter((r) => r.kind === "gpa_min");
  const creditReqs = data.requirements.filter((r) => r.kind === "credit_min");
  const metaTitle = `${p.name.split(",")[0]} Transfer Guide`;
  const metaDesc = `Complete transfer guide for ${p.name}: prerequisites, GPA, credit thresholds, and links to community-college-specific pathways. Sourced from official catalogs.`;

  return (
    <div className="min-h-screen bg-bone" data-testid="mishap-program-landing">
      <SEO
        title={metaTitle}
        description={metaDesc}
        canonical={`/mishap-corrector/programs/${p.slug}`}
        ogType="article"
        jsonLd={[buildBreadcrumbJsonLd(p, origin), buildCourseJsonLd(p, origin)]}
      />
      <TopNav />
      <main className="max-w-4xl mx-auto px-5 py-14">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Program Guide</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight leading-[1.05]" data-testid="program-h1">
          {p.name}
        </h1>
        {p.notes && <p className="mt-6 text-ink-soft leading-relaxed max-w-3xl">{p.notes}</p>}

        <section className="mt-10 grid sm:grid-cols-2 gap-4">
          {(creditReqs[0] || gpaReqs[0]) && (
            <>
              <div className="border border-brass/60 p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Minimum GPA</div>
                <div className="mt-2 font-serif text-3xl text-ink tabular-nums">{gpaReqs[0]?.gpa_threshold || 2.0}</div>
              </div>
              <div className="border border-brass/60 p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Credits</div>
                <div className="mt-2 font-serif text-3xl text-ink tabular-nums">{creditReqs[0]?.credit_threshold || 60}</div>
              </div>
            </>
          )}
        </section>

        {prereqCourses.length > 0 && (
          <section className="mt-12" data-testid="program-prereqs">
            <h2 className="font-serif text-2xl text-ivy tracking-tight">Prerequisites</h2>
            <div className="mt-4 border border-stone/30 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ivy text-bone text-left uppercase tracking-[0.18em] text-[10px]">
                  <tr>
                    <th className="px-4 py-3">Course</th>
                    <th className="px-4 py-3">Min grade</th>
                    <th className="px-4 py-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {prereqCourses.map((r, i) => (
                    <tr key={i} className={i % 2 ? "bg-bone-dim" : "bg-bone"}>
                      <td className="px-4 py-3 font-mono text-ink">{r.course_code}</td>
                      <td className="px-4 py-3 text-ink">{r.min_grade || "C"}</td>
                      <td className="px-4 py-3 text-ink-soft">{r.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="mt-12" data-testid="program-sources">
          <h2 className="font-serif text-2xl text-ivy tracking-tight">Official sources</h2>
          <ul className="mt-4 space-y-3">
            {data.verification_records.map((r) => (
              <li key={r.record_id} className="border-l-2 border-brass pl-4">
                <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                   className="text-ivy hover:text-brass font-medium text-sm underline-offset-2 hover:underline">
                  {r.source_title}
                </a>
                {r.notes && <p className="text-ink-soft text-xs mt-1 leading-relaxed">{r.notes}</p>}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 border border-ivy bg-ivy/5 p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Free diagnostic</div>
          <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">Run a diagnostic for this program</h2>
          <p className="mt-2 text-ink-soft text-sm max-w-2xl">
            Upload your transcript (or enter courses manually) to see where your current path lines up — or diverges — from the {p.name} tracking sheet.
          </p>
          <Link
            to={`/mishap-corrector?program=${p.slug}`}
            className="mt-5 inline-block border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 hover:bg-brass hover:border-brass"
            data-testid="program-diagnostic-cta"
          >
            Run your free diagnostic →
          </Link>
        </section>

        {data.pair_community_colleges.length > 0 && (
          <section className="mt-14" data-testid="program-pair-links">
            <h2 className="font-serif text-2xl text-ivy tracking-tight">Community colleges that transfer into this program</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {data.pair_community_colleges.map((cc) => (
                <li key={cc.slug}>
                  <Link
                    to={`/transfer-guides/${cc.slug}/${p.slug}`}
                    className="block border border-stone/30 px-4 py-3 text-sm text-ink hover:border-ivy hover:text-ivy transition-colors"
                    data-testid={`program-pair-link-${cc.slug}`}
                  >
                    {cc.name} → {p.name.split(",")[0]}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
